import React from 'react';
import { Download } from 'lucide-react';
import { Input } from 'components/ui/input';
import { Combobox } from 'components/ui/combobox';
import Dropdowns from 'components/Dropdowns';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { CNHoldLevel, CNHoldLookupOption, CNHoldRow, fetchCNHoldCustomers, fetchCNHoldData, fetchCNHoldStockLocations, updateCNHoldDropdowns, downloadCNHold } from './api';
import CNHoldTable from './components/CNHoldTable';

type SortDirection = 'asc' | 'desc';
type ComboboxOption = {
    value: string;
    label: string;
};

const holdReasonOptions: ComboboxOption[] = [
    {
        value: 'Hold by Customer',
        label: 'Hold by Customer',
    },
    {
        value: 'CONSIGNEE COPY AWAITED',
        label: 'CONSIGNEE COPY AWAITED',
    },
];

const CNHoldReportPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();

    const [levels, setLevels] = React.useState<CNHoldLevel[]>([]);
    const [dropdownLoading, setDropdownLoading] = React.useState<boolean>(false);
    const [dropdownError, setDropdownError] = React.useState<string>('');

    const [lookupLoading, setLookupLoading] = React.useState<boolean>(false);

    const [customerOptions, setCustomerOptions] = React.useState<ComboboxOption[]>([]);
    const [stockLocationOptions, setStockLocationOptions] = React.useState<ComboboxOption[]>([]);

    const [selectedCustomer, setSelectedCustomer] = React.useState<string>('');
    const [selectedStockLocation, setSelectedStockLocation] = React.useState<string>('');
    const [selectedHoldReason, setSelectedHoldReason] = React.useState<string>('');

    const [search, setSearch] = React.useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');

    const [tableRows, setTableRows] = React.useState<CNHoldRow[]>([]);
    const [tableLoading, setTableLoading] = React.useState<boolean>(false);
    const [tableError, setTableError] = React.useState<string>('');

    const [page, setPage] = React.useState<number>(1);
    const [perPage, setPerPage] = React.useState<number>(20);
    const [totalItems, setTotalItems] = React.useState<number>(0);

    const [sortBy, setSortBy] = React.useState<keyof CNHoldRow>('cn_number');
    const [sortOrder, setSortOrder] = React.useState<SortDirection>('asc');

    const [downloadingExcel, setDownloadingExcel] = React.useState<boolean>(false);

    const buildFilters = React.useCallback(
        (currentLevels: CNHoldLevel[]) => {
            const filters: Record<string, string[]> = {};

            currentLevels.forEach((level) => {
                if (level.selected && level.selected.length > 0) {
                    filters[String(level.id)] = level.selected;
                }
            });

            return filters;
        }, []);

    const selectedFiltersKey = React.useMemo(() => JSON.stringify(buildFilters(levels)), [buildFilters, levels]);

    const selectedFilters = React.useMemo(() =>
        JSON.parse(
            selectedFiltersKey || '{}'
        ) as Record<string, string[]>, [selectedFiltersKey]);

    const fetchDropdowns = React.useCallback(async (selection: Record<string, string[]> = {}) => {
        setDropdownLoading(true);
        setDropdownError('');
        try {
            const resp = await updateCNHoldDropdowns({ selection });
            setLevels(Array.isArray(resp?.levels) ? resp.levels : []);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load filters';
            setDropdownError(message);
            setLevels([]);
        } finally {
            setDropdownLoading(false);
        }
    }, []);

    const fetchCustomers = React.useCallback(async (selection: Record<string, string[]> = {}, stockLocation?: string) => {
        try {
            const resp = await fetchCNHoldCustomers({ filters: selection, stock_location: stockLocation });
            const options = (resp?.items ?? []).map(
                (item: CNHoldLookupOption) => ({
                    value: item.code,
                    label: item.name,
                })
            );
            setCustomerOptions(options);
            const codes = new Set(options.map((o) => o.value));
            setSelectedCustomer((prev) => prev && !codes.has(prev) ? '' : prev);
        } catch {
            setCustomerOptions([]);
        }
    }, []);

    const fetchStockLocations = React.useCallback(async (selection: Record<string, string[]> = {}, custCode?: string) => {
        try {
            const resp = await fetchCNHoldStockLocations({
                filters: selection,
                cust_code: custCode,
            });

            const options = (resp?.items ?? []).map(
                (item: CNHoldLookupOption) => ({
                    value: item.code,
                    label: item.name,
                })
            );
            setStockLocationOptions(options);
            const codes = new Set(options.map((o) => o.value));
            setSelectedStockLocation((prev) => prev && !codes.has(prev) ? '' : prev);
        } catch {
            setStockLocationOptions([]);
        }
    }, []);

    const fetchTableData = React.useCallback(async (currentFilters: Record<string, string[]>, pageOverride?: number, sortOverride?: keyof CNHoldRow, orderOverride?: SortDirection, perPageOverride?: number) => {
        setTableLoading(true);
        setTableError('');
        try {
            const resp = await fetchCNHoldData({
                filters: currentFilters,
                search: debouncedSearch.trim() || undefined,
                cust_code: selectedCustomer || undefined,
                stock_location: selectedStockLocation || undefined,
                hold_reason: selectedHoldReason || undefined,
                page: pageOverride ?? page,
                per_page: perPageOverride ?? perPage,
                sort_by: String(sortOverride ?? sortBy),
                sort_order: orderOverride ?? sortOrder,
            });

            setTableRows(Array.isArray(resp?.rows) ? resp.rows : []);
            setTotalItems(Number(resp?.total_items || 0));
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to fetch report';
            setTableError(message);
            setTableRows([]);
            setTotalItems(0);
        } finally {
            setTableLoading(false);
        }
    }, [debouncedSearch, selectedCustomer, selectedStockLocation, selectedHoldReason, page, perPage, sortBy, sortOrder]);

    const handleDownloadExcel = React.useCallback(async () => {
        try {
            setDownloadingExcel(true);
            setTableError('');

            const blob = await downloadCNHold({
                filters: selectedFilters,
                search: debouncedSearch.trim() || undefined,
                cust_code: selectedCustomer || undefined,
                stock_location: selectedStockLocation || undefined,
                hold_reason: selectedHoldReason || undefined,
                sort_by: String(sortBy),
                sort_order: sortOrder,
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'cn_stock_report.xlsx';

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to download Excel';
            setTableError(message);
        } finally {
            setDownloadingExcel(false);
        }
    }, [selectedFilters, debouncedSearch, selectedCustomer, selectedStockLocation, selectedHoldReason, sortBy, sortOrder]);

    React.useEffect(() => {
        setBreadcrumbs([
            { label: 'Reports', href: '/reports/pnl' },
            { label: 'CN Stock' },
        ]);
    }, [setBreadcrumbs]);

    React.useEffect(() => {
        setPage(1);
        setLevels([]);

        setSearch('');
        setDebouncedSearch('');

        setSelectedCustomer('');
        setSelectedStockLocation('');
        setSelectedHoldReason('');

        fetchDropdowns({});

        setLookupLoading(true);

        Promise.all([
            fetchCustomers({}),
            fetchStockLocations({}),
        ]).finally(() => setLookupLoading(false));
    }, [fetchDropdowns, fetchCustomers, fetchStockLocations]);

    React.useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 400);

        return () => window.clearTimeout(timer);
    }, [search]);

    React.useEffect(() => {
        fetchTableData(selectedFilters, page, sortBy, sortOrder, perPage);
    }, [selectedFilters, debouncedSearch, selectedCustomer, selectedStockLocation, selectedHoldReason, page, perPage, sortBy, sortOrder, fetchTableData]);

    const handleSelectChange = React.useCallback(
        (levelId: number | string, value: string[]) => {
            const desc = (levels || [])
                .filter((l) => Boolean(l.visible))
                .sort((a, b) => Number(b.id) - Number(a.id));

            const indexById = Object.fromEntries(
                desc.map((l, i) => [String(l.id), i])
            );

            const changedIdx = indexById[String(levelId)];

            const currentLevels = levels.map((level) => {
                const idx = indexById[String(level.id)];

                if (idx === changedIdx) {
                    return {
                        ...level,
                        selected: value.length ? value : null,
                    };
                }

                if (idx > changedIdx) {
                    return {
                        ...level,
                        selected: null,
                    };
                }

                return level;
            });

            setLevels(currentLevels);
            const selection = buildFilters(currentLevels);
            fetchDropdowns(selection);
            setLookupLoading(true);

            Promise.all([
                fetchCustomers(selection, selectedStockLocation || undefined),
                fetchStockLocations(selection, selectedCustomer || undefined),
            ]).finally(() =>
                setLookupLoading(false)
            );
            setPage(1);
        }, [levels, buildFilters, fetchDropdowns, fetchCustomers, fetchStockLocations, selectedCustomer, selectedStockLocation]);

    const toggleSort = React.useCallback(
        (column: keyof CNHoldRow) => {
            const nextOrder: SortDirection = sortBy === column && sortOrder === 'asc' ? 'desc' : 'asc';

            setSortBy(column);
            setSortOrder(nextOrder);
            setPage(1);
        },
        [sortBy, sortOrder]
    );

    const handlePageChange = React.useCallback(
        (nextPage: number) => {
            setPage(Math.max(1, nextPage));
        }, []);

    return (
        <div className="mx-auto flex h-full min-h-0 w-full max-w-screen-2xl flex-col gap-6 px-4 sm:px-6 2xl:max-w-[2000px]">
            <div className="shrink-0 space-y-4">
                <Dropdowns
                    showYear={false}
                    levels={levels}
                    loading={dropdownLoading}
                    onSelectChange={handleSelectChange}
                />

                {dropdownError && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3">
                        <p className="text-sm text-red-600">{dropdownError}</p>
                    </div>
                )}
            </div>

            <div className="grid shrink-0 items-end gap-4 lg:grid-cols-[1fr_1fr_1fr_1fr_auto]">
                <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Search CN No.</label>

                    <Input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Type CN number..."
                    />
                </div>

                <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Customer</label>

                    <Combobox
                        options={customerOptions}
                        value={selectedCustomer}
                        onValueChange={(value) => {
                            setSelectedCustomer(value);
                            setLookupLoading(true);
                            fetchStockLocations(selectedFilters, value).finally(() =>
                                setLookupLoading(false)
                            );
                            setPage(1);
                        }}
                        placeholder={lookupLoading ? 'Loading customers...' : 'All customers'}
                        searchPlaceholder="Search customer..."
                        emptyText="No customer found."
                        disabled={lookupLoading}
                    />
                </div>

                <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Stock Location</label>

                    <Combobox
                        options={stockLocationOptions}
                        value={selectedStockLocation}
                        onValueChange={(value) => {
                            setSelectedStockLocation(value);
                            setLookupLoading(true);
                            fetchCustomers(selectedFilters, value).finally(() =>
                                setLookupLoading(false)
                            );
                            setPage(1);
                        }}
                        placeholder={lookupLoading ? 'Loading locations...' : 'All locations'}
                        searchPlaceholder="Search stock location..."
                        emptyText="No stock location found."
                        disabled={lookupLoading}
                    />
                </div>

                <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Hold Reason</label>

                    <Combobox
                        options={holdReasonOptions}
                        value={selectedHoldReason}
                        onValueChange={(value) => {
                            setSelectedHoldReason(value);
                            setPage(1);
                        }}
                        placeholder="All hold reasons"
                        searchPlaceholder="Search hold reason..."
                        emptyText="No hold reason found."
                    />
                </div>

                <div className="flex justify-end">
                    <button
                        className="px-3 py-1 text-sm rounded-md border border-green-600 text-green-700 hover:bg-green-50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleDownloadExcel}
                        disabled={downloadingExcel}
                    >
                        <Download size={16} />
                        {downloadingExcel ? 'Downloading...' : 'Excel'}
                    </button>
                </div>
            </div>

            {tableError && (
                <div className="shrink-0 rounded-md border border-red-200 bg-red-50 p-3">
                    <p className="text-sm text-red-600">
                        {tableError}
                    </p>
                </div>
            )}

            <div className="min-h-0 flex-1">
                <CNHoldTable
                    data={tableRows}
                    loading={tableLoading}
                    page={page}
                    pageSize={perPage}
                    totalItems={totalItems}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={toggleSort}
                    onPageChange={handlePageChange}
                    onPageSizeChange={(size) => {
                        setPerPage(size);
                        setPage(1);
                    }}
                />
            </div>
        </div>
    );
};

export default CNHoldReportPage;